import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Wine, Leaf, Cigarette, Gift, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

const cardConfig = {
  alcohol: {
    title: 'Carte Alcool',
    icon: Wine,
    reward: 'Bouteille gratuite',
    description: '1 tampon par bouteille achetée',
    color: '#722F37',
    secondaryColor: '#F3E5E7',
    accentColor: '#D4AF37',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/ssv1zkgy_1.png',
    glowColor: 'rgba(114, 47, 55, 0.6)'
  },
  cbd: {
    title: 'Carte CBD',
    icon: Leaf,
    reward: 'Sachet 2g gratuit',
    description: '1 tampon par achat de 2g',
    color: '#2D5A45',
    secondaryColor: '#E8F5E9',
    accentColor: '#66BB6A',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/5qfu6xxl_5.png',
    glowColor: 'rgba(45, 90, 69, 0.6)'
  },
  puffs: {
    title: 'Carte Puffs',
    icon: Cigarette,
    reward: 'Puff gratuit par ligne',
    description: '1 tampon par puff acheté',
    color: '#6366F1',
    secondaryColor: '#EEF2FF',
    accentColor: '#06B6D4',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/f35iwlob_3.png',
    glowColor: 'rgba(99, 102, 241, 0.6)',
    lines: ['20k', '28k', '32k', '36k']
  }
};

// Floating particles component
const FloatingParticles = ({ color, count = 6 }) => {
  const particles = useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: Math.random() * 3,
      duration: 3 + Math.random() * 2,
      size: 4 + Math.random() * 4
    })), [count]
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute bottom-0 rounded-full"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
            boxShadow: `0 0 ${p.size * 2}px ${color}`
          }}
          animate={{
            y: [0, -120],
            x: [0, (Math.random() - 0.5) * 40],
            opacity: [0, 1, 1, 0],
            scale: [1, 1.2, 0.8]
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeOut"
          }}
        />
      ))}
    </div>
  );
};

export default function ReadOnlyLoyaltyCard({ card, isInactive = false }) {
  const config = cardConfig[card.type];
  const Icon = config.icon;
  
  const stampedCount = card.boxes.filter(b => b).length;
  const totalBoxes = card.type === 'puffs' ? 40 : 10;
  const isNearCompletion = card.type !== 'puffs' && (10 - stampedCount) <= 2 && (10 - stampedCount) > 0;
  const isActive = !isInactive && stampedCount > 0;
  
  // Vérifier si la carte est prête à échanger (depuis le backend OU calcul local)
  const isReadyToRedeem = card.readyToRedeem || (card.type !== 'puffs' && stampedCount === 10);
  
  // Check if any line is complete for puffs
  const completedLines = card.type === 'puffs' 
    ? config.lines.map((_, idx) => {
        const lineStart = idx * 10;
        const lineEnd = lineStart + 10;
        return card.boxes.slice(lineStart, lineEnd).every(b => b);
      })
    : [];
  
  const hasCompletedLine = completedLines.some(completed => completed);
  const isFullyComplete = card.type !== 'puffs' && stampedCount === 10;

  const renderStampGrid = () => {
    if (card.type === 'puffs') {
      // Trier les lignes : actives en premier
      const sortedLines = config.lines.map((lineName, lineIdx) => {
        const lineStart = lineIdx * 10;
        const lineEnd = lineStart + 10;
        const lineBoxes = card.boxes.slice(lineStart, lineEnd);
        const lineStamped = lineBoxes.filter(b => b).length;
        return { lineName, lineIdx, lineStart, lineBoxes, lineStamped };
      }).sort((a, b) => {
        if (a.lineStamped > 0 && b.lineStamped === 0) return -1;
        if (a.lineStamped === 0 && b.lineStamped > 0) return 1;
        return b.lineStamped - a.lineStamped;
      });

      return (
        <div className="space-y-4">
          {sortedLines.map(({ lineName, lineIdx, lineStart, lineBoxes, lineStamped }) => {
            const lineCompleted = lineBoxes.every(b => b);
            const lineRemaining = 10 - lineStamped;
            const lineNearCompletion = lineRemaining <= 2 && lineRemaining > 0;
            const isLineInactive = lineStamped === 0;

            return (
              <motion.div
                key={lineIdx}
                whileHover={{ scale: 1.01 }}
                className={cn(
                  'p-4 rounded-xl border-2 transition-all relative overflow-hidden',
                  lineNearCompletion && 'animate-pulse',
                  lineCompleted && 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-500',
                  isLineInactive && 'opacity-50'
                )}
                style={{
                  borderColor: lineCompleted ? '#10B981' : lineNearCompletion ? '#8B5CF6' : isLineInactive ? '#CBD5E1' : '#E2E8F0',
                  backgroundColor: isLineInactive ? '#F8FAFC' : undefined
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm" style={{ color: isLineInactive ? '#94A3B8' : config.color }}>
                      Ligne {lineName}
                    </span>
                    {isLineInactive && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">
                        Inactive
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-semibold" style={{ color: isLineInactive ? '#94A3B8' : config.color }}>
                    {lineStamped}/10
                  </span>
                  {lineCompleted && (
                    <motion.div 
                      className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-600 text-xs font-bold"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <Gift className="w-3 h-3" />
                      Puff gratuit !
                    </motion.div>
                  )}
                </div>
                <div className="grid grid-cols-10 gap-2">
                  {lineBoxes.map((isStamped, boxIdx) => (
                    <motion.div
                      key={boxIdx}
                      animate={isStamped ? { 
                        scale: [1, 1.15, 1],
                        boxShadow: [
                          `0 0 0px ${config.color}`,
                          `0 0 12px ${config.color}`,
                          `0 0 0px ${config.color}`
                        ]
                      } : {}}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: boxIdx * 0.1 }}
                      className={cn(
                        'aspect-square rounded-lg border-2 flex items-center justify-center transition-all',
                        isStamped && 'shadow-md'
                      )}
                      style={{
                        backgroundColor: isStamped ? config.color : 'white',
                        borderColor: isStamped ? config.color : isLineInactive ? '#E2E8F0' : '#CBD5E1'
                      }}
                    >
                      {isStamped && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-5 gap-3">
        {card.boxes.map((isStamped, index) => (
          <motion.div
            key={index}
            animate={isStamped ? { 
              scale: [1, 1.15, 1],
              boxShadow: [
                `0 0 0px ${config.color}`,
                `0 0 15px ${config.color}`,
                `0 0 0px ${config.color}`
              ]
            } : {}}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: index * 0.15 }}
            className={cn(
              'aspect-square rounded-xl border-2 flex items-center justify-center transition-all',
              isStamped && 'shadow-md'
            )}
            style={{
              backgroundColor: isStamped ? config.color : 'white',
              borderColor: isStamped ? config.color : '#CBD5E1'
            }}
          >
            {isStamped && <CheckCircle2 className="w-6 h-6 text-white" />}
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02, y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={cn(
        'relative bg-white rounded-3xl shadow-xl overflow-hidden mb-6',
        isActive && 'loyalty-card-active',
        (isNearCompletion || hasCompletedLine) && 'ring-2 ring-purple-500 ring-offset-2',
        isInactive && 'opacity-80'
      )}
      style={{
        borderWidth: '3px',
        borderStyle: 'solid',
        borderColor: isNearCompletion || hasCompletedLine ? '#8B5CF6' : isActive ? config.color : 'transparent',
        boxShadow: isActive 
          ? `0 10px 40px ${config.glowColor}, 0 0 0 1px ${config.color}20`
          : '0 10px 40px rgba(0,0,0,0.1)'
      }}
    >
      {/* Badge Actif/Inactif avec Sparkle */}
      <motion.div 
        className={cn(
          'absolute top-4 right-4 z-20 px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1',
          isInactive 
            ? 'bg-gray-100 text-gray-500 border border-gray-300' 
            : 'bg-green-100 text-green-600 border border-green-300'
        )}
        animate={!isInactive ? { scale: [1, 1.05, 1] } : {}}
        transition={{ duration: 2, repeat: Infinity }}
      >
        {!isInactive && <Sparkles className="w-3 h-3" />}
        {isInactive ? '○ Inactive' : '✓ Active'}
      </motion.div>

      {/* Header with AMAZING Background Image Effects */}
      <div
        className="relative h-36 flex items-center justify-between px-6 overflow-hidden loyalty-card-header"
        style={{
          filter: isInactive ? 'grayscale(50%)' : 'none'
        }}
      >
        {/* Background Image Layer with Breathing Effect */}
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${config.bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
          }}
          animate={!isInactive ? {
            scale: [1, 1.05, 1],
          } : {}}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Gradient Overlay - Plus transparent pour voir l'image */}
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${config.color}99 0%, ${config.accentColor}80 100%)`,
          }}
        />

        {/* Shimmer Effect */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              transform: 'translateX(-100%)'
            }}
            animate={{
              x: ['-100%', '200%']
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
              repeatDelay: 1
            }}
          />
        </div>

        {/* Floating Particles for Active Cards */}
        {isActive && <FloatingParticles color={config.accentColor} count={8} />}

        {/* Card Content */}
        <div className="relative z-10 text-white">
          <motion.div 
            className="flex items-center gap-3 mb-2"
            animate={isActive ? { x: [0, 2, 0] } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div
              animate={isActive ? { 
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              } : {}}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Icon className="w-8 h-8 drop-shadow-lg" />
            </motion.div>
            <h2 className="text-2xl font-bold drop-shadow-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {config.title}
            </h2>
          </motion.div>
          <p className="text-sm opacity-90 drop-shadow">{config.description}</p>
        </div>

        {/* Stamp Counter with Pulse */}
        <motion.div 
          className="relative z-10 text-white text-right"
          animate={isActive ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <motion.div 
            className="text-4xl font-bold drop-shadow-lg"
            style={{ 
              textShadow: isActive ? `0 0 20px ${config.accentColor}` : 'none' 
            }}
          >
            {card.type === 'puffs' ? `${stampedCount}/40` : `${stampedCount}/10`}
          </motion.div>
          <div className="text-xs opacity-90 uppercase tracking-wider">tampons</div>
        </motion.div>

        {/* Bottom Glow Line */}
        {isActive && (
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-1"
            style={{
              background: `linear-gradient(90deg, transparent, ${config.accentColor}, transparent)`
            }}
            animate={{
              opacity: [0.5, 1, 0.5],
              scaleX: [0.8, 1, 0.8]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        )}
      </div>

      {/* Card Body */}
      <div className="p-6 relative">
        {renderStampGrid()}

        {/* Reward Info with Animation */}
        <motion.div
          className={cn(
            "mt-6 p-4 rounded-xl flex items-center justify-between relative overflow-hidden",
            isReadyToRedeem && "ring-2 ring-green-500"
          )}
          style={{ backgroundColor: isInactive ? '#F1F5F9' : isReadyToRedeem ? '#DCFCE7' : config.secondaryColor }}
          whileHover={{ scale: 1.02 }}
          animate={isReadyToRedeem ? { scale: [1, 1.02, 1] } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="flex items-center gap-3 relative z-10">
            <motion.div
              animate={!isInactive ? { rotate: [0, 15, -15, 0] } : {}}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Gift className="w-6 h-6" style={{ color: isInactive ? '#94A3B8' : isReadyToRedeem ? '#16A34A' : config.color }} />
            </motion.div>
            <div>
              <div className="font-semibold" style={{ color: isInactive ? '#64748B' : isReadyToRedeem ? '#16A34A' : config.color }}>
                {isInactive ? 'Commencez à collecter !' : isReadyToRedeem ? '🎉 À ÉCHANGER !' : 'Cadeau gratuit'}
              </div>
              <div className="text-sm text-gray-600">
                {isInactive 
                  ? 'Achetez pour obtenir des tampons'
                  : isReadyToRedeem || hasCompletedLine
                    ? '🎉 Réclamez votre cadeau en magasin !'
                    : config.reward
                }
              </div>
            </div>
          </div>
          {(isReadyToRedeem || hasCompletedLine) && (
            <motion.div 
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-sm"
              animate={{ 
                scale: [1, 1.1, 1],
                boxShadow: [
                  '0 0 0px rgba(16, 185, 129, 0)',
                  '0 0 20px rgba(16, 185, 129, 0.6)',
                  '0 0 0px rgba(16, 185, 129, 0)'
                ]
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              🎁 À échanger !
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Warning Messages */}
      {card.type === 'alcohol' && (
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 italic">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span>L'abus d'alcool est dangereux pour la santé, à consommer avec modération</span>
          </div>
        </div>
      )}
      {card.type === 'puffs' && (
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2 text-xs text-orange-500 italic">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span>La nicotine est fortement addictive. Usage non recommandé aux non-fumeurs.</span>
          </div>
        </div>
      )}
      {card.type === 'cbd' && (
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2 text-xs text-amber-500 italic">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span>Le CBD peut altérer la vigilance. Ne conduisez pas après consommation.</span>
          </div>
        </div>
      )}
    </motion.div>
  );
}
